import React, { Fragment, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { VisuallyHidden } from '@coursera/cds-core';

import _t from 'i18n!nls/discussions';

import type { InputVariables, PostsForYouTabDataProviderProps, ResponseVariables } from './__types__';

const GetPostsForYouQuery = gql`
  query GetPostsForYouQuery($input: InputVariables!) {
    GetPostsForYouResponse(input: $input)
      @rest(type: "GetPostsForYouResponse", path: "grpc/forum/post/v1/PostsForYouAPI/GetPostsForYou", method: "POST") {
      postResults
      nextPageToken
      totalHits
    }
  }
`;

const PostsForYouTabDataProvider: React.FC<PostsForYouTabDataProviderProps> = ({
  courseId,
  currentPage,
  hasBeenVisited,
  children,
}) => {
  const [notificationText, setNotification] = useState('');

  const { data, loading, error } = useQuery<ResponseVariables, InputVariables>(GetPostsForYouQuery, {
    variables: {
      input: {
        course_id: courseId,
        page_size: 15,
        page_token: ((currentPage - 1) * 15).toString(),
      },
    },
    skip: !courseId,
    context: { clientName: 'gatewayGql' },
  });

  const defaultResponse = { postResults: [], nextPageToken: '', totalHits: '0' };
  const postsForYouResponse = data?.GetPostsForYouResponse || defaultResponse;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const setAsyncNotification = (func: () => void) => {
      timeoutId = setTimeout(func, 0);
    };

    if (loading) {
      setAsyncNotification(() => setNotification(_t('Loading posts')));
    } else if (!postsForYouResponse.postResults.length) {
      setAsyncNotification(() => setNotification(_t('No posts yet')));
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [loading, postsForYouResponse, hasBeenVisited]);

  return (
    <Fragment>
      <VisuallyHidden aria-live="assertive" aria-atomic="true" role="alert">
        {!hasBeenVisited && postsForYouResponse.postResults.length ? (
          <FormattedMessage
            message={_t('Loaded {numOfPosts} {numOfPosts, plural, =1 {post} other {posts}}')}
            numOfPosts={postsForYouResponse.postResults.length}
          />
        ) : (
          notificationText
        )}
      </VisuallyHidden>
      {children({ loading, error, data: postsForYouResponse })}
    </Fragment>
  );
};

export default PostsForYouTabDataProvider;
